import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { PublishHelpDialogProps } from '../../utils/contentTypes'
import { Share2 } from 'lucide-react'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog"
import { BlogPostHelp } from './help-content/BlogPostHelp'
import { LandingPageHelp } from './help-content/LandingPageHelp'
import { ToolHelp } from './help-content/ToolHelp'
import { EmailCampaignHelp } from './help-content/EmailCampaignHelp'
import { AdGroupHelp } from './help-content/AdGroupHelp'
import { SocialPostHelp } from './help-content/SocialPostHelp'
import { GenericHelp } from './help-content/GenericHelp'

/**
 * Dialog with help about publishing different content types
 */
export function PublishHelpDialog({
    isOpen,
    onClose,
    contentType
}: PublishHelpDialogProps) {
    // Get help content based on content type
    const getHelpContent = () => {
        switch(contentType) {
            case 'blog_post':
                return <BlogPostHelp />
            case 'landing_page':
                return <LandingPageHelp />
            case 'tool':
                return <ToolHelp />
            case 'email_campaign':
                return <EmailCampaignHelp />
            case 'ad_group':
                return <AdGroupHelp />
            case 'social_post':
                return <SocialPostHelp />
            default:
                return <GenericHelp contentType={contentType} />
        }
    }

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-2xl">
                <DialogHeader className="space-y-2">
                    <DialogTitle className="flex items-center gap-2">
                        <Share2 className="h-5 w-5 text-primary" />
                        How to Publish This Content
                    </DialogTitle>
                </DialogHeader>

                <div className="relative flex flex-col h-[calc(100vh-12rem)]">
                    <ScrollArea className="flex-1 -mx-6 px-6">
                        <div className="py-4 space-y-6">
                            {getHelpContent()}
                            
                            <div className="bg-secondary/30 p-4 rounded-lg border border-border">
                                <h4 className="font-medium mb-2">General Publishing Tips</h4>
                                <ul className="list-disc pl-5 space-y-2">
                                    <li>Make sure to review your content thoroughly before publishing.</li>
                                    <li>Test all interactive elements and links to ensure they work as expected.</li>
                                    <li>Consider using the <Badge variant="outline" className="mx-1 font-normal">Improve Content</Badge> tool to optimize your content before publishing.</li>
                                    <li>If you need to make changes after publishing, simply update the content and republish.</li>
                                    <li>Use the <Badge variant="outline" className="mx-1 font-normal">Version History</Badge> button to revert to previous versions if needed.</li>
                                </ul>
                            </div>
                        </div>
                    </ScrollArea>

                    <div className="py-4 flex justify-end border-t mt-auto">
                        <Button onClick={onClose}>Close</Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    )
}